import { SyncRecordStatus, SyncRecord, SyncChanges } from '../types/sync-state';
import Dexie, {
  IndexableType,
  PromiseExtended,
  Table,
  Transaction,
} from 'dexie';
import { tlLogger } from '@taleemabad/shared';
import { userCache } from '../utils/user-cache';
import { UserRole } from '../types/user';

/**
 * Module-level flag to distinguish server-pull updates (extractSyncResponse)
 * from user-initiated updates. When true, the updateHook preserves the incoming
 * syncStatus instead of falling through to the 'Updated' catch-all.
 *
 * Without this, bulkPut on an already-synced record (where syncStatus doesn't
 * change) causes the hook to fall through, flipping the record to 'updated'
 * and creating an infinite push-pull loop.
 */
let _isServerPull = false;
export function setServerPullFlag(value: boolean): void {
  _isServerPull = value;
}

type PrimaryKeyArray = number[] | string[];
export abstract class BaseModel extends Dexie {
  constructor(dbName: string) {
    super(dbName);
  }

  protected setupTableHooks(tables: Table[]) {
    tables.forEach((table) => {
      table.hook('updating', this.updateHook.bind(this));
      table.hook('deleting', this.deleteHook.bind(this));
      table.hook('creating', this.createHook.bind(this));
    });
  }

  updateHook(
    modifications: Object,
    primKey: IndexableType,
    obj: any,
    transaction: Transaction,
  ): any {
    const result = {
      ...obj,
      ...modifications,
    };

    // skip sync update if only 'lastModifiedAt' has changed
    // TODO: make this more generic
    if (
      Object.keys(modifications).length === 1 &&
      'lastModifiedAt' in modifications
    ) {
      return;
    }

    // Server-pull deletes always win — local flags (isNewCreated, CreatedButNotSynced)
    // must not block a server-authoritative delete.
    if (
      _isServerPull &&
      'syncStatus' in modifications &&
      (modifications as any).syncStatus === SyncRecordStatus.Deleted
    ) {
      return this.updateSyncRecord(SyncRecordStatus.Deleted, result, transaction);
    }

    // Never resurrect a server-deleted record via a non-syncStatus field update.
    // Two known callers without syncStatus in modifications that hit Deleted rows:
    //   1. level-snapshot patch in fetchCourseData (.modify({ level: {...} }))
    //   2. reapplyLocalTrainingStatuses (.modify({ trainingStatus: status }))
    // Both run with _isServerPull=false and would otherwise fall through to
    // updateSyncRecord(Updated), resurrecting the deleted record.
    // This guard is intentionally global across all BaseModel tables: deleted
    // records are hidden in the UI so no local user action can reach them —
    // the only callers of .modify() on Deleted rows are background patches like
    // the two above, which must not change sync eligibility.
    if (
      obj.syncStatus === SyncRecordStatus.Deleted &&
      !('syncStatus' in modifications)
    ) {
      return; // accept field changes, preserve Deleted status
    }

    // skip sync update if only 'isNewCreated' is true in Student/User data
    // TODO: make this more generic
    if ('isNewCreated' in obj && obj.isNewCreated === true) {
      return;
    }

    if (
      'syncStatus' in obj &&
      obj.syncStatus === SyncRecordStatus.CreatedButNotSynced
    ) {
      // Allow LP-followed + MCQ updates to promote the row to Updated; otherwise
      // we never run updateSyncRecord(Updated) and is_lp_followed can stay wrong.
      const promotesToUpdated =
        'syncStatus' in modifications &&
        (modifications as { syncStatus?: string }).syncStatus ===
          SyncRecordStatus.Updated;
      if (!promotesToUpdated) {
        obj.lastLocalModifiedAt = Date.now();
        return;
      }
    }

    if (
      'syncStatus' in modifications &&
      modifications.syncStatus === SyncRecordStatus.Failed
    ) {
      return this.updateSyncRecord(
        SyncRecordStatus.Failed,
        result,
        transaction,
      );
    }

    if (
      'syncStatus' in modifications &&
      modifications.syncStatus === SyncRecordStatus.Deleted
    ) {
      return this.updateSyncRecord(
        SyncRecordStatus.Deleted,
        result,
        transaction,
      );
    }

    if (
      'syncStatus' in modifications &&
      modifications.syncStatus === SyncRecordStatus.Updated
    ) {
      return this.updateSyncRecord(
        SyncRecordStatus.Updated,
        result,
        transaction,
      );
    }

    if (
      'syncStatus' in modifications &&
      modifications.syncStatus === SyncRecordStatus.Synced
    ) {
      return this.updateSyncRecord(
        SyncRecordStatus.Synced,
        result,
        transaction,
      );
    }

    // Server-pull guard: when extractSyncResponse is doing a bulkPut,
    // preserve the record's current syncStatus instead of flipping to 'updated'.
    // User-initiated updates don't set this flag, so they still fall through correctly.
    if (_isServerPull) {
      const status =
        'syncStatus' in modifications
          ? (modifications as any).syncStatus
          : obj.syncStatus;
      return this.updateSyncRecord(status, result, transaction);
    }

    return this.updateSyncRecord(SyncRecordStatus.Updated, result, transaction);
  }

  deleteHook(primKey: IndexableType, obj: any, transaction: Transaction) {
    return this.updateSyncRecord(SyncRecordStatus.Deleted, obj, transaction);
  }

  createHook(primKey: IndexableType, obj: any, transaction: Transaction) {
    const profile = userCache.getUserFromCache();
    if (profile) {
      obj.user = profile.id;
      const selectedProfile = profile.profiles.filter((p) => p.isSelected)?.[0];
      if (selectedProfile?.type === UserRole.Teacher) {
        obj.profile = selectedProfile.id;
      }
    }
    //TODO: implement this logic and make this more generic
    if (
      'syncStatus' in obj &&
      obj.syncStatus === SyncRecordStatus.CreatedButNotSynced
    ) {
      obj.lastLocalModifiedAt = Date.now();
      return;
    }
    if ('syncStatus' in obj && obj.syncStatus === SyncRecordStatus.Synced) {
      this.updateSyncRecord(SyncRecordStatus.Synced, obj, transaction);
    } else {
      this.updateSyncRecord(SyncRecordStatus.Created, obj, transaction);
    }
  }

  updateSyncRecord(
    status: SyncRecordStatus,
    obj: any,
    transaction: Transaction,
  ) {
    tlLogger.verbose(
      `${transaction.db.name}/${
        transaction.storeNames
      }/hook:${status}/${JSON.stringify(obj)}`,
    );
    // Mutate obj for createHook (creating hook relies on obj mutation directly).
    obj.syncStatus = status;
    // ponytail: return delta only — Dexie updating hook contract: return value
    // is merged as additional modifications, NOT a replacement. Returning the
    // full obj (which includes compound PK paths profileId/id) silently defeats
    // the syncStatus write on compound-key tables (coursesV2, trainingsV2).
    const delta: Record<string, any> = { syncStatus: status };
    if (!_isServerPull) {
      const now = Date.now();
      obj.lastLocalModifiedAt = now;
      delta.lastLocalModifiedAt = now;
    }
    return delta;
  }
}

export abstract class BaseModelForPush extends BaseModel {
  _updateSyncData(
    model: Table,
    ids: PrimaryKeyArray, // it can be different the ids
    status: SyncRecordStatus,
  ): Promise<number> {
    const primaryKey = model.schema.primKey.keyPath ?? 'id'; // Default to 'id' if primary key is not  uuid

    return model
      .where(primaryKey)
      .anyOf(ids)
      .modify({ syncStatus: status.toString() });
  }

  _getSyncData(model: Table): PromiseExtended<SyncRecord[]> {
    return model
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Created.toString(), SyncRecordStatus.Failed.toString()])
      .toArray();
  }

  abstract getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>>;
  abstract updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number>;
}
